import { ThemeProvider } from "@emotion/react";
import { CssBaseline } from "@mui/material";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import i18n from "i18next";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { initReactI18next } from "react-i18next";
import { RouterProvider } from "react-router";
import { Toaster } from "sonner";
import { z } from "zod";

import { processSignOut } from "Auth/Hooks/useSignOut";
import { JWT_KEY } from "Auth/Models/AuthConstants";
import type { FetchResponse } from "Infrastructure/Api/Api";
import { getJwtKey, setupClient } from "Infrastructure/Api/Api";
import { handleResponseError } from "Infrastructure/Api/ApiUtils";
import { getTheme } from "Infrastructure/Themes/Themes";
import { CzechResourcesDictionary } from "Infrastructure/Translations/CzechResourcesDictionary";
import { DeutschResourcesDictionary } from "Infrastructure/Translations/DeutschResourcesDictionary";
import { EnglishResourcesDictionary } from "Infrastructure/Translations/EnglishResourcesDictionary";
import {
  getSavedOrDetectedLanguage,
  saveLanguagePreference,
} from "Infrastructure/Utils/LanguageUtils";
import { getZodLocale } from "Infrastructure/Utils/LocaleUtils";
import { initSentry } from "Infrastructure/Utils/SentryUtils";
import { trackUserIdentification } from "Infrastructure/Utils/TrackingUtils";
import { CurrencyProvider } from "Intl/Context/CurrencyProvider";
import { appRouter } from "./Infrastructure/Routing/AppRouter";

const SHOW_DEVTOOLS = import.meta.env.VITE_SHOW_DEVTOOLS === "true";

const initialLanguage = getSavedOrDetectedLanguage();

function App() {
  initSentry();

  const theme = getTheme();
  i18n.use(initReactI18next).init({
    resources: {
      cs: CzechResourcesDictionary,
      en: EnglishResourcesDictionary,
      de: DeutschResourcesDictionary,
    },
    lng: initialLanguage,
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
  });

  z.config(getZodLocale());

  i18n.on("languageChanged", (lng) => {
    z.config(getZodLocale());
    saveLanguagePreference(lng);
  });

  const responseMiddleware = {
    name: "auth-middleware",
    fn: <TData, TStatus extends number>(
      response: FetchResponse<TData, TStatus>,
    ): FetchResponse<TData, TStatus> => {
      const token = getJwtKey();
      if (token !== undefined && token !== null && response.status === 401) {
        processSignOut(queryClient);
      } else {
        trackUserIdentification();
      }
      return response;
    },
  };

  setupClient({
    apiUrl: `${import.meta.env.VITE_API_URL}`,
    jwtKey: JWT_KEY,
    requestMiddlewares: [],
    responseMiddlewares: [responseMiddleware],
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      mutations: {
        onError: handleResponseError,
      },
    },
    queryCache: new QueryCache({
      onError: handleResponseError,
    }),
  });

  return (
    <QueryClientProvider client={queryClient}>
      {SHOW_DEVTOOLS && (
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-right"
        />
      )}
      <ThemeProvider theme={theme}>
        <Toaster richColors position="bottom-center" />
        <CssBaseline />
        <CurrencyProvider>
          <NuqsAdapter>
            <RouterProvider router={appRouter} />
          </NuqsAdapter>
        </CurrencyProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
export default App;
